import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { useIntersect } from 'global/hooks/useIntersect';
import { LinearProgress } from '@mui/material';
import NoData from './NoData';
import ErrorAlert from './ErrorAlert';
import { get, size, isEqual, concat, map } from 'global/lib/lodash';

/*
  This HOC takes an array of items and returns them as
  a list of the specified component type. It will load
  the list dynamically based on a step-count, scroll,
  and simple search criteria.

  Takes the following Components as args:
  * ListItemWrapped: The list item component to render
    (such as <ListItem /> for Material UI)
    This component will inherit passed through props
  * ListWrapped: the list item wrapper
    (such as <List /> for Material UI)

  Requires the following props:
  * data
    An array of the data necessary to load items
*/

const withInfiniteScroll = (ListItemWrapped, ListWrapped) => {
  return withRouter((props) => {
    const { data, currentPage, handlePageChange, lastPage, loading, alwaysReplaceData, trackChecks } = props;
    const [lastData, setLastData] = useState(false);
    const [displayData, setDisplayData] = useState([]);

    const [ref, inView] = useIntersect({ rootMargin: '0px' });

    useEffect(() => {
      if (get(inView, 'isIntersecting') === true && !loading) {
        const newPage = currentPage + 1;
        handlePageChange(newPage);
      }
    }, [inView]);

    useEffect(() => {
      if (alwaysReplaceData) {
        setDisplayData(data);
      } else if (data && !isEqual(lastData, data) && !isEqual(displayData, data)) {
        const newData = currentPage === 1 ? data : concat(displayData, data);
        setDisplayData(newData);
      } else if (!data && !loading) {
        setDisplayData([]);
      }
      setLastData(data);
    }, [data, loading]);

    const showChecks = (() => {
      if (!trackChecks) {
        return;
      }

      if (size(displayData) && size(displayData)) {
        const newT = trackChecks.filter((nt) => nt.fromSelected).map((t) => t.id);
        return size(displayData.filter((d) => newT.indexOf(d.id) > -1));
      } else {
        return false;
      }
    })();

    return (
      <>
        {props.header}
        {size(displayData) ? (
          <>
            <div style={{ height: 4 }}>{loading && <LinearProgress />}</div>
            <ListWrapped>
              {map(displayData, (d, index) => {
                const keyData = Array.isArray(d) ? d[0] : d;
                const keyIndex = props?.keyIndex && get(keyData, props.keyIndex) || keyData.id || index
                return (
                <ListItemWrapped path={props.location.pathname} index={index} key={keyIndex} item={d} showChecks={showChecks} {...props} />
              )})}
            </ListWrapped>
            {(currentPage !== lastPage || loading) && <div ref={ref} style={{ height: 20 }}>{loading && <LinearProgress />}</div>}
          </>
        ) : get(props, 'error') ? (
          <ErrorAlert error={get(props, 'error')} />
        ) : (
          <NoData {...props} loading={loading} />
        )}
      </>
    );
  });
};

export default withInfiniteScroll;
